/* eslint-disable class-methods-use-this */
// Methods can't be static as they are externally called
import settings from 'settings';
import moment from 'moment-timezone';
import { history } from 'store';
// import { Redirect } from 'react-router-dom';
import * as cookies from 'library/cookies';
import JwtService from './jwt-service';
import MsalService from './msal-service';

class AuthService {
  constructor(config) {
    this.CONSTANTS = {
      AUTH_TYPE: 'AUTH_TYPE',
      AUTH_TYPE_JWT: 'AUTH_TYPE_JWT',
      AUTH_TYPE_MSAL: 'AUTH_TYPE_MSAL',
      MSAL_IS_AUTHENTICATED: 'MSAL_IS_AUTHENTICATED'
    };

    if (config) {
      this.config = config;
    }
    else {
      throw new Error('Auth config parameters not specified');
    }
  }

  runWithMsal(app, doNotLogin) {
    MsalService.runWithMsal(app, doNotLogin);
  }

  loginJwt(username, password) {
    this.setCacheItem(this.CONSTANTS.AUTH_TYPE, this.CONSTANTS.AUTH_TYPE_JWT);
    return JwtService.login(username, password)
      .then(response => response,
        (error) => {
          this.removeCacheItem(this.CONSTANTS.AUTH_TYPE);
          throw error;
        });
  }

  loginMsal() {
    this.setCacheItem(this.CONSTANTS.AUTH_TYPE, this.CONSTANTS.AUTH_TYPE_MSAL);
    MsalService.login();
  }

  logout() {
    switch (this.getAuthType()) {
    case this.CONSTANTS.AUTH_TYPE_JWT:
      this.removeCacheItem(this.CONSTANTS.AUTH_TYPE);
      JwtService.logout();
      this.redirectToLogin();
      break;

    case this.CONSTANTS.AUTH_TYPE_MSAL:
      // -- MSAL will contact the Azure AD Service to logout
      // -- and redirect back to the login back so no need
      // -- to perform a redirect back to the login page here.
      // ---
      this.removeCacheItem(this.CONSTANTS.AUTH_TYPE);
      this.removeCacheItem(this.CONSTANTS.MSAL_IS_AUTHENTICATED);
      MsalService.logout();
      break;
    default:
      break;
    }

    // Reset timezone to local time
    moment.tz.setDefault();
  }

  checkLoginOption() {
    // -- Check if the user has selected a login option and
    // -- process accordingly.
    // ---
    if (this.getAuthType() === this.CONSTANTS.AUTH_TYPE_MSAL) {
      // -- User selected MSAL login option. Check if the user was previously
      // -- authenticated and if yes, redirect to the MSAL login to refetch
      // -- the token and redirect back to the site.

      // -- This situation could occur when:
      // -- 1. The user had previously logged in, closed their browser tab
      // -- without correctly logging out and navigated back to the site.
      // -- In this case the user is still authenticated so we want to
      // -- redirect them back into the site without logging in again.
      // --
      // -- 2. The user logged in successfully and opened up a new browser
      // -- session tab. We want to enable SSO across all browser tab session.
      // -- The MSAL authentication token is generated and cached per tab session
      // -- A new tab will required the token to be regenerated and cached
      // -- for this session. In this case, redirect the user to the MSAL login
      // -- page to regenerate the token and redirect them back to the site
      // -- without logging in.
      // ---
      const isAuthenticated = JSON.parse(this.getCacheItem(this.CONSTANTS.MSAL_IS_AUTHENTICATED));
      if (isAuthenticated) {
        this.loginMsal();
      }
    }
  }

  redirectToLogin() {
    switch (this.getAuthType()) {
    case this.CONSTANTS.AUTH_TYPE_JWT:
      history.push(settings.LOGIN_URL());
      break;

    case this.CONSTANTS.AUTH_TYPE_MSAL:
      this.loginMsal();
      break;

    default:
      history.push(settings.LOGIN_URL());
      break;
    }
  }

  redirectToTenancy() {
    history.push(settings.SITE_TENANCY_URL());
  }

  redirectToHomePage() {
    history.push(settings.DASHBOARD_URL()
      .replace(document.location.origin, ''));
  }

  isAuthenticated() {
    let isAuthenticated = false;

    switch (this.getAuthType()) {
    case this.CONSTANTS.AUTH_TYPE_JWT:
      isAuthenticated = JwtService.isAuthenticated();
      break;

    case this.CONSTANTS.AUTH_TYPE_MSAL:
      isAuthenticated = MsalService.isAuthenticated();
      if (isAuthenticated) {
        this.setCacheItem(this.CONSTANTS.MSAL_IS_AUTHENTICATED, isAuthenticated);
      }
      break;

    default:
      break;
    }
    return isAuthenticated;
  }

  getAuthType() {
    return this.getCacheItem(this.CONSTANTS.AUTH_TYPE);
  }

  getAuthHeader(requestUrl) {
    switch (this.getAuthType()) {
    case this.CONSTANTS.AUTH_TYPE_JWT:
      return JwtService.getAuthHeader(requestUrl);

    case this.CONSTANTS.AUTH_TYPE_MSAL:
      return MsalService.getAuthHeader(requestUrl);

    default:
      break;
    }

    return null;
  }

  getUser() {
    let user = {};

    switch (this.getAuthType()) {
    case this.CONSTANTS.AUTH_TYPE_JWT:
      user = JwtService.getUser();
      break;

    case this.CONSTANTS.AUTH_TYPE_MSAL:
      user = MsalService.getUser();
      break;

    default:
      break;
    }

    // Set the default timezone based on the current tenancy
    if (user && user.currentTenancy) {
      const { timezone } = user.currentTenancy;
      moment.tz.setDefault(timezone);
    }
    return user;
  }

  whoami() {
    let user = {};

    switch (this.getAuthType()) {
    case this.CONSTANTS.AUTH_TYPE_JWT:
      user = JwtService.getCachedUser();
      break;

    case this.CONSTANTS.AUTH_TYPE_MSAL:
      user = MsalService.getCachedUser();
      break;

    default:
      break;
    }
    return user;
  }

  setCurrentTenancy(tenancy) {
    switch (this.getAuthType()) {
    case this.CONSTANTS.AUTH_TYPE_JWT:
      JwtService.setCurrentTenancy(tenancy);
      break;

    case this.CONSTANTS.AUTH_TYPE_MSAL:
      MsalService.setCurrentTenancy(tenancy);
      break;

    default:
      break;
    }

    // Set the default timezone based on the current tenancy
    if (tenancy) {
      const { timezone } = tenancy;
      moment.tz.setDefault(timezone);
    }
  }

  // -- FUNCTION: getCacheItem
  // -- Searches the value for the given key in the cache
  // ---
  getCacheItem = function (key) {
    if (this.config && this.config.cacheLocation) {
      // Use localStorage
      if (this.config.cacheLocation === 'localStorage') {
        if (!this.supportsLocalStorage()) {
          return null;
        }
        return localStorage.getItem(key);
      }

      // Use cookieStorage
      if (this.config.cacheLocation === 'cookieStorage') {
        return cookies.getCookie(key);
      }
    }

    // Default as sessionStorage
    if (!this.supportsSessionStorage()) {
      return null;
    }
    return sessionStorage.getItem(key);
  };

  // -- FUNCTION: setCacheItem
  // -- Saves the key-value pair in the cache
  // ---
  setCacheItem(key, obj) {
    if (this.config && this.config.cacheLocation) {
      // Use localStorage
      if (this.config.cacheLocation === 'localStorage') {
        if (!this.supportsLocalStorage()) {
          return false;
        }
        localStorage.setItem(key, obj);
        return true;
      }

      // Use cookieStorage
      if (this.config.cacheLocation === 'cookieStorage') {
        cookies.setCookie(key, obj);
        return true;
      }
    }

    // Default as session storage
    if (!this.supportsSessionStorage()) {
      return false;
    }

    sessionStorage.setItem(key, obj);
    return true;
  }

  // -- FUNCTION: removeCacheItem
  // -- Saves the key-value pair in the cache
  // ---
  removeCacheItem(key) {
    if (this.config && this.config.cacheLocation) {
      // Use localStorage
      if (this.config.cacheLocation === 'localStorage') {
        if (!this.supportsLocalStorage()) {
          return false;
        }
        localStorage.removeItem(key);
        return true;
      }

      // Use cookieStorage
      if (this.config.cacheLocation === 'cookieStorage') {
        cookies.removeCookie(key);
        return true;
      }
    }

    // Default as session storage
    if (!this.supportsSessionStorage()) {
      return false;
    }

    sessionStorage.removeItem(key);
    return true;
  }

  // -- FUNCTION: supportsLocalStorage
  // -- Returns true if browser supports localStorage, false otherwise.
  // ---
  supportsLocalStorage = function () {
    try {
      if (!window.localStorage) return false; // Test availability
      window.localStorage.setItem('storageTest', 'A'); // Try write
      if (window.localStorage.getItem('storageTest') !== 'A') return false; // Test read/write
      window.localStorage.removeItem('storageTest'); // Try delete
      if (window.localStorage.getItem('storageTest')) return false; // Test delete
      return true; // Success
    }
    catch (e) {
      return false;
    }
  };

  // -- FUNCTION: supportsSessionStorage
  // -- Returns true if browser supports sessionStorage, false otherwise.
  // ---
  supportsSessionStorage = function () {
    try {
      if (!window.sessionStorage) return false; // Test availability
      window.sessionStorage.setItem('storageTest', 'A'); // Try write
      if (window.sessionStorage.getItem('storageTest') !== 'A') return false; // Test read/write
      window.sessionStorage.removeItem('storageTest'); // Try delete
      if (window.sessionStorage.getItem('storageTest')) return false; // Test delete
      return true; // Success
    }
    catch (e) {
      return false;
    }
  };
}

export default new AuthService(settings.AUTH_CONFIG);
